import {
    TimeSeriesItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { formatDate } from "~/utils/functions/formatDate";
import { Emdash } from "~/components/Punctuation/Emdash";
import { formatApy } from "~/utils/functions/formatApy";
import { DataGridPercentValue } from "~/components/DataGrid/DataGridPercentValue";
import React from "react";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { cn } from "~/lib/utils";

// // // //

function calculatePercentageValues(
    array: Array<TimeSeriesItem & { percentage?: number }>,
) {
    // Calculate the sum of all 'value' fields
    const totalValue = array.reduce((sum, item) => sum + item.points[0][1], 0);

    // Iterate through the array and add the 'percentage' property
    for (let i = 0; i < array.length; i++) {
        const item = array[i];
        const percentage = item.points[0][1] / totalValue;
        array[i].percentage = percentage;
    }

    return array;
}

// // // //

function BroadridgeTooltipRow(props: {
    label: string;
    backgroundColor?: string | null;
    stacked: boolean;
    unit: TimeSeriesUnits;
    percentValue?: number;
    value: number | null;
    isContractData?: boolean;
}) {
    const {
        label,
        backgroundColor,
        stacked,
        percentValue,
        unit,
        value,
        isContractData = false,
    } = props;

    return (
        <div className="flex justify-between">
            <p className="flex text-chart-body items-center">
                <span
                    className="h-3 w-3 rounded-full mr-2"
                    style={{
                        backgroundColor: backgroundColor || "#999999",
                    }}
                />
                <span className="mr-2">{label}</span>
            </p>
            <p className="flex items-center text-chart-body ml-4">
                <span className="font-mono">
                    {stacked && (
                        <span className="tabular-nums">
                            {percentValue ? (
                                <DataGridPercentValue value={percentValue} />
                            ) : (
                                <Emdash />
                            )}
                        </span>
                    )}

                    {!stacked && (
                        <>
                            {unit === TimeSeriesUnits.token && (
                                <>
                                    {value ? (
                                        Math.round(value).toLocaleString()
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.address && (
                                <>
                                    {value ? (
                                        // For contract data, don't show dollar sign
                                        isContractData ? (
                                            value.toLocaleString()
                                        ) : (
                                            value.toLocaleString()
                                        )
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.year && (
                                <>{value ? value + " years" : <Emdash />}</>
                            )}
                            {(unit === TimeSeriesUnits.dollar ||
                                unit === TimeSeriesUnits.dollars) && (
                                <>
                                    {value ? (
                                        // For contract data, don't show dollar sign
                                        isContractData ? (
                                            Math.round(value).toLocaleString()
                                        ) : (
                                            "$" +
                                            Math.round(value).toLocaleString()
                                        )
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.percent && (
                                <>
                                    {value ? (
                                        formatApy(value) + "%"
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </span>
            </p>
        </div>
    );
}

// // // //

/**
 * BroadridgeTooltipContent
 * Render content for Broadridge chart tooltip with special handling for contract data
 */
export function BroadridgeTooltipContent(props: {
    x: string;
    data: Array<TimeSeriesItem & { percentage?: number }>;
    unit: TimeSeriesUnits;
    stacked: boolean;
    showTotal: boolean;
    maxTooltipEntries?: number;
}) {
    const { unit, stacked, maxTooltipEntries } = props;

    let showTotal = props.showTotal;
    let data = props.data;

    // Don't show total if there's only one series in the tooltip
    if (data.length === 1) {
        showTotal = false;
    }

    if (stacked) {
        data = calculatePercentageValues(data);
    }

    // Compute total sum to display in tooltip
    let sum = -1;
    if (showTotal) {
        sum = data.reduce((total: number, each: TimeSeriesItem) => {
            const pointValue = each.points[0] ? each.points[0][1] : 0;
            return total + pointValue;
        }, 0);
    }

    // Define flag if unit is dollar or dollars
    const isDollar = [TimeSeriesUnits.dollar, TimeSeriesUnits.dollars].includes(
        unit,
    );

    // Limit the number of entries in the tooltip
    const limitMaxEntries =
        maxTooltipEntries && data.length > maxTooltipEntries;

    let dataToRender: Array<TimeSeriesItem & { percentage?: number }> = [
        ...data,
    ];
    let allOthersSum: number = 0;
    let allOthersPercent: number = 0;
    let allOthersEntry: Array<TimeSeriesItem & { percentage?: number }> = [];
    if (limitMaxEntries) {
        dataToRender = data.slice(0, maxTooltipEntries);
        allOthersEntry = data.slice(maxTooltipEntries);

        allOthersSum = allOthersEntry.reduce(
            (total: number, each: TimeSeriesItem & { percentage?: number }) => {
                const pointValue = each.points[0] ? each.points[0][1] : 0;
                return total + pointValue;
            },
            0,
        );

        allOthersPercent = allOthersEntry.reduce(
            (total: number, each: TimeSeriesItem & { percentage?: number }) => {
                const pointValue = each.percentage || 0;
                return total + pointValue;
            },
            0,
        );
    }

    return (
        <div className="flex flex-col select-none p-2 min-w-72">
            <div
                className={cn("flex items-center justify-between mb-2", {
                    "pb-1 border-b": showTotal,
                })}
            >
                <p className="text-chart-lg tabular-nums tracking-wider">
                    {formatDate(props.x)}
                </p>
                {/* Show total only for repo volumes, not contracts */}
                {showTotal && sum > -1 && (
                    <div className="flex items-center">
                        <p className="text-chart-body font-mono tabular-nums">
                            {/* Display dollar total for repo volumes only */}
                            {isDollar && (
                                <>
                                    <span>$</span>
                                    {formatCurrencyValue(sum)}
                                </>
                            )}
                            {/* Display non-dollar total */}
                            {!isDollar && <>{sum.toLocaleString()}</>}
                        </p>
                    </div>
                )}
            </div>

            {dataToRender.map((d) => {
                const value = d.points[0] ? d.points[0][1] : null;

                // Check if this is contract data (contains "contract" in the name)
                const isContractData = d.group.name
                    .toLowerCase()
                    .includes("contract");

                // Use the individual metric's unit instead of the shared unit
                const metricUnit = d.measure.unit;

                return (
                    <BroadridgeTooltipRow
                        key={d.group.id}
                        label={d.group.name}
                        backgroundColor={d.group.color}
                        stacked={stacked}
                        value={value}
                        percentValue={d.percentage}
                        unit={metricUnit}
                        isContractData={isContractData}
                    />
                );
            })}

            {limitMaxEntries && (
                <BroadridgeTooltipRow
                    label="All Others"
                    stacked={stacked}
                    value={allOthersSum}
                    percentValue={allOthersPercent}
                    unit={unit}
                />
            )}
        </div>
    );
}
